import classNames from 'classnames'
import DotsLoading from 'components/loadings/dotsLoading'
import posthog from 'posthog-js'
import React, { useEffect, useRef, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { BankConvertor } from 'utilities/helper/banksConvertor'
import { storedMemberId } from 'pages/dashboard/transactions/hooks/useMemberId'
import { matchesAccount, SEARCH_THRESHOLD } from './accountSearch'
import InlineAddAccount from './InlineAddAccount'
interface IModalProps {
    show: boolean
    onHide: () => void
    data: Array<IBankAccountCards> | undefined | false
    activeAccounts: IBankAccountCards[] | undefined
    otherAccounts: IBankAccountCards[] | undefined
    selectedUser: string | undefined
    setSelectedBankNumber: React.Dispatch<React.SetStateAction<number>>
    handleSelectCard: (id: number) => void
    /**
     * Whose payout this is. An account added here is always registered to
     * the member who is signed in, so it can only land in the list when the
     * money is going to them; for anyone else the form would be a dead end.
     * Absent means the old behaviour (treated as the signed-in member).
     */
    receiverMemberId?: number
}
export default function WithDrawWalletModal({
    show,
    onHide,
    data,
    activeAccounts,
    otherAccounts,
    selectedUser,
    setSelectedBankNumber,
    handleSelectCard,
    receiverMemberId,
}: IModalProps) {
    const isForSelf =
        receiverMemberId == null || receiverMemberId === storedMemberId()
    const [query, setQuery] = useState('')
    const [isAdding, setIsAdding] = useState(false)
    const [addedId, setAddedId] = useState<number>()
    const searchReported = useRef(false)

    useEffect(() => {
        if (!show) {
            setQuery('')
            setIsAdding(false)
            searchReported.current = false
        }
    }, [show])

    // Select the account just added once the refetched list carries it. The
    // timeout matters: the parent's useGetCards effect runs after this one on
    // the same data and puts the selection back on the default account.
    useEffect(() => {
        if (!addedId || !data || !data.some((item) => item.id === addedId))
            return
        const id = addedId
        setAddedId(undefined)
        setTimeout(() => {
            setSelectedBankNumber(id)
            handleSelectCard(id)
        }, 0)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, addedId])

    const total = (activeAccounts?.length ?? 0) + (otherAccounts?.length ?? 0)
    const showSearch = total >= SEARCH_THRESHOLD
    const visibleActive = activeAccounts?.filter((item) =>
        matchesAccount(item, query)
    )
    const visibleOther = otherAccounts?.filter((item) =>
        matchesAccount(item, query)
    )
    const nothingFound =
        !!query.trim() && !visibleActive?.length && !visibleOther?.length

    const onSearch = (value: string) => {
        setQuery(value)
        if (value.trim() && !searchReported.current) {
            searchReported.current = true
            posthog.capture('payout_account_search_used', {
                accounts_count: total,
            })
        }
    }

    const renderAccount = (item: IBankAccountCards) => (
        <div
            className={classNames(
                ' flex justify-start rounded-lg py-2 px-3 items-center  my-2',
                item.isSelected ? 'bg-header-bg' : 'bg-gray-5'
            )}
            onClick={() => {
                setSelectedBankNumber(item.id)
                handleSelectCard(item.id)
            }}
            key={item.id}
        >
            <img
                src={BankConvertor[item.bank_unique_code]}
                alt="bank"
                className=" w-10 h-10"
            />
            <div className=" mr-3 min-w-0">
                <p className="break-words">{item.full_name}</p>
                <p className=" mt-2">
                    {item.card_number
                        ? item.card_number.match(/.{1,4}/g)?.join(' ')
                        : item.iban.match(/.{1,4}/g)?.join(' ')}
                </p>
            </div>
        </div>
    )

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="sm"
            aria-labelledby="contained-modal-title-vcenter"
            centered
        >
            <div className=" py-3 px-2 text-sm">
                <p className=" text-center text-sm">
                    یک شماره حساب را به عنوان حساب اصلی صندوق انتخاب کنید:
                </p>
                <hr className=" my-2" />
                {isAdding ? (
                    <InlineAddAccount
                        onAdded={(id) => {
                            setIsAdding(false)
                            setQuery('')
                            setAddedId(id)
                        }}
                        onCancel={() => setIsAdding(false)}
                    />
                ) : isForSelf ? (
                    <button
                        type="button"
                        className="bg-green-main text-green-1  rounded-md py-2 my-2 w-full text-sm"
                        onClick={() => {
                            posthog.capture('payout_picker_add_opened', {
                                for_self: true,
                            })
                            setIsAdding(true)
                        }}
                    >
                        ثبت شماره حساب جدید
                    </button>
                ) : (
                    <p className="bg-gray-bg rounded-md p-2 my-2 text-xs leading-6">
                        حسابی که از اینجا ثبت شود به نام خود شما ثبت می‌شود و در
                        فهرست حساب‌های {selectedUser} نمی‌آید. {selectedUser}{' '}
                        باید حساب خودش را از «مدیریت کارت‌های بانکی» در همیان
                        ثبت کند.
                    </p>
                )}
                {showSearch && !isAdding && (
                    <input
                        className="w-full rounded-md bg-gray-bg py-2 px-3 my-2 outline-none"
                        type="search"
                        autoComplete="off"
                        placeholder="جستجوی نام، بانک یا شماره کارت"
                        value={query}
                        onChange={(e) => onSearch(e.target.value)}
                    />
                )}
                {data ? (
                    <div className=" h-96 overflow-y-scroll">
                        <p> حساب های {selectedUser}</p>
                        {activeAccounts && activeAccounts.length === 0 && (
                            <p className="text-gray-1 text-xs my-2">
                                {selectedUser} هنوز حساب تأییدشده‌ای در همیان
                                ثبت نکرده است.
                            </p>
                        )}
                        {visibleActive && visibleActive.map(renderAccount)}
                        {visibleOther && visibleOther.length > 0 && (
                            <>
                                <hr className=" my-2" />
                                <p className=" my-2">حساب های شما</p>
                                {visibleOther.map(renderAccount)}
                            </>
                        )}
                        {nothingFound && (
                            <p className="text-gray-1 text-center my-4">
                                حسابی با این مشخصات پیدا نشد
                            </p>
                        )}
                    </div>
                ) : (
                    <DotsLoading />
                )}

                <div>
                    <hr className=" my-2" />
                    <p
                        className=" text-green-1 text-center cursor-pointer"
                        onClick={onHide}
                    >
                        تایید
                    </p>
                </div>
            </div>
        </Modal>
    )
}
